import { Component, HostListener, OnInit } from '@angular/core';
import { ArkUserType, MetricDefinitionModel } from '../../core/api/models';
import { MetricLibraryApiService } from '../../core/api/services';
import { AuthService } from '../../main/auth.service';
import { AlertService } from '../../shared/services/alert.service';
import { Utils } from '../../core/utils';

@Component({ selector: 'app-metric-library', templateUrl: './metric-library.component.html', standalone: false })
export class MetricLibraryComponent implements OnInit {
  rows: MetricDefinitionModel[] = [];
  selected?: MetricDefinitionModel;
  loading = false;
  saving = false;
  search = '';
  error = '';
  original = '';
  calculations = ['Percentage that meets a condition', 'Number of records', 'Total', 'Average', 'Lowest value', 'Highest value', 'Ratio (one amount divided by another)',
    'Completed on time', 'Time taken to complete', 'Yes / No', 'Streak (not available yet)', 'Compare an average with a goal',
    'Required items completed out of items due', 'Custom formula (not available yet)'];
  frequencies = [{ value: 'Daily', label: 'Daily' }, { value: 'Weekly', label: 'Weekly' }, { value: 'Monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' }, { value: 'ReviewPeriod', label: 'KRA review period' }, { value: 'RollingDays', label: 'Rolling number of days' }];

  constructor(private api: MetricLibraryApiService, private auth: AuthService, private alerts: AlertService) { }

  ngOnInit() { this.auth.checkIfAutorized([ArkUserType.Admin]); this.load(); }

  load() {
    this.loading = true;
    this.api.List().subscribe({ next: rows => { this.rows = rows; this.loading = false; },
      error: err => { this.error = Utils.getAjaxError(err); this.loading = false; } });
  }

  get definitions() {
    const search = this.search.toLocaleLowerCase();
    return this.rows.filter(x => !this.rows.some(y => y.ID === x.ID && (y.Version ?? 0) > (x.Version ?? 0)))
      .filter(x => `${x.Name} ${x.Category}`.toLocaleLowerCase().includes(search));
  }

  get history() { return this.rows.filter(x => x.ID === this.selected?.ID).sort((a, b) => (b.Version ?? 0) - (a.Version ?? 0)); }

  get hasUnsavedChanges() { return !!this.selected && JSON.stringify(this.selected) !== this.original; }

  canLeave() { return !this.saving && (!this.hasUnsavedChanges || window.confirm('Leave this page and discard your changes?')); }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent) {
    if (this.saving || this.hasUnsavedChanges) { event.preventDefault(); event.returnValue = ''; }
  }

  hasFrequency(value: string) { return (this.selected?.SupportedFrequencies || '').split(',').some(x => x.trim() === value); }

  setFrequency(value: string, checked: boolean) {
    if (!this.selected || this.saving) { return; }
    const values = (this.selected.SupportedFrequencies || '').split(',').map(x => x.trim()).filter(x => x && x !== value);
    if (checked) { values.push(value); }
    this.selected.SupportedFrequencies = values.join(',');
  }

  choose(row?: MetricDefinitionModel) {
    if (this.saving) { return; }
    if (this.hasUnsavedChanges && !window.confirm('Discard your changes and choose another metric?')) { return; }
    this.selected = row ? JSON.parse(JSON.stringify(row)) : { ID: 0, VersionID: 0, Active: true, Code: '', Name: '',
      Category: '', MetricType: '', Units: '', Description: '', CalculationType: 0, RequiredInputs: '',
      CalculationDescription: '', SupportedFrequencies: 'ReviewPeriod', SupportsAutomation: false,
      AllowsManualEntry: true, GamificationEligible: false };
    this.original = JSON.stringify(this.selected);
    this.error = '';
  }

  save() {
    if (this.saving || !this.selected) { return; }
    this.saving = true;
    this.error = '';
    const submitted = JSON.parse(JSON.stringify(this.selected));
    this.api.Save({ body: submitted }).subscribe({ next: result => {
      this.rows.filter(x => x.ID === result.ID).forEach(x => x.Active = result.Active);
      this.rows.push(result);
      this.selected = JSON.parse(JSON.stringify(result));
      this.original = JSON.stringify(this.selected);
      this.saving = false;
      this.alerts.success('Metric saved as a new version. KRAs already using this metric keep their current version.');
    }, error: err => { this.error = Utils.getAjaxError(err); this.saving = false; } });
  }
}
